import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { cn, formatDate, getStatusColor, getRecordTypeLabel } from '../lib/utils';
import { 
  ShieldCheck, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Calendar, 
  User, 
  Building, 
  AlertTriangle, 
  Loader2,
  Car,
  Cylinder,
  Wrench,
  FileText,
  Minus
} from 'lucide-react';

export default function PublicVerifyPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadVerification();
  }, [token]);

  const loadVerification = async () => {
    try {
      const response = await publicAPI.verify(token);
      setData(response.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Verification not found' : 'Failed to verify');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to display test result with three states
  const getTestResultDisplay = (passed) => {
    if (passed === true) {
      return { text: 'Pass', icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-100' };
    } else if (passed === false) {
      return { text: 'Fail', icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' };
    }
    return { text: 'Not Conducted', icon: Minus, color: 'text-gray-500', bg: 'bg-gray-100' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-6">
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="font-heading text-xl font-bold mb-2">Verification Failed</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <p className="text-sm text-muted-foreground">
              The QR code may be invalid or the inspection record no longer exists.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isValid = data.status === 'valid';
  const isExpired = data.status === 'expired';

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Header */}
      <header className="border-b bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg">CNG Inspection Portal</h1>
              <p className="text-xs text-muted-foreground">Tanzania Regulatory Authority</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Status Card */}
        <Card className={cn(
          "mb-6 border-2",
          isValid ? "border-emerald-200 bg-emerald-50/50" :
          isExpired ? "border-red-200 bg-red-50/50" :
          "border-amber-200 bg-amber-50/50"
        )}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className={cn(
                "h-16 w-16 rounded-full flex items-center justify-center",
                isValid ? "bg-emerald-100" : isExpired ? "bg-red-100" : "bg-amber-100"
              )}>
                {isValid ? (
                  <CheckCircle className="h-10 w-10 text-emerald-600" />
                ) : isExpired ? (
                  <XCircle className="h-10 w-10 text-red-600" />
                ) : (
                  <AlertTriangle className="h-10 w-10 text-amber-600" />
                )}
              </div>
              <div>
                <Badge 
                  variant="outline"
                  className={cn(
                    "text-lg px-4 py-1 font-bold uppercase",
                    isValid ? "border-emerald-500 bg-emerald-500 text-white" :
                    isExpired ? "border-red-500 bg-red-500 text-white" :
                    "border-amber-500 bg-amber-500 text-white"
                  )}
                >
                  {data.status}
                </Badge>
                <p className={cn(
                  "text-sm mt-1",
                  isValid ? "text-emerald-700" : isExpired ? "text-red-700" : "text-amber-700"
                )}>
                  {isValid ? 'This inspection certificate is valid' : 
                   isExpired ? 'This inspection certificate has expired' : 
                   'Inspection status requires attention'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Certificate Info */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Certificate Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Certificate No</p>
                <p className="font-mono font-bold text-primary">{data.certificate_number || 'N/A'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Verification No</p>
                <p className="font-mono font-medium">{data.verification_number}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle/Trailer/Storage Details */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Car className="h-5 w-5 text-primary" />
              {data.record_type === 'cng_storage' ? 'Storage Information' : 
               data.record_type === 'trailer' ? 'Trailer Information' : 
               'Vehicle Information'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  {data.record_type === 'cng_storage' ? 'Equipment Type' : 
                   data.record_type === 'trailer' ? 'Asset Type' : 
                   'Vehicle Type'}
                </p>
                <p className="font-medium">{getRecordTypeLabel(data.record_type)}</p>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  {data.record_type === 'cng_storage' ? 'Serial Number' : 
                   data.registration_number ? 'Registration Number' : 'Storage Serial'}
                </p>
                <p className="font-mono font-bold text-lg">
                  {data.record_type === 'cng_storage' 
                    ? (data.serial_number || data.storage_serial_number)
                    : (data.registration_number || data.storage_serial_number)}
                </p>
              </div>

              {/* Manufacturer (Trailer/Storage) or Make/Model (Vehicle) */}
              {(data.record_type === 'trailer' || data.record_type === 'cng_storage') ? (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Manufacturer</p>
                  <p className="font-medium">{data.manufacturer || data.make || 'N/A'}</p>
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Make / Model</p>
                  <p className="font-medium">{data.make || 'N/A'} {data.model || ''}</p>
                </div>
              )}

              {/* Product Name (Trailer/Storage) or Fuel Type (Vehicle) */}
              {(data.record_type === 'trailer' || data.record_type === 'cng_storage') ? (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Product Name</p>
                  <p className="font-medium">{data.product_name || data.fuel_type || 'N/A'}</p>
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Fuel Type</p>
                  <p className="font-medium">{data.fuel_type ? data.fuel_type.replace('_', '-').toUpperCase() : 'N/A'}</p>
                </div>
              )}

              {/* Storage: Facility Category; Trailer: Product Serial */}
              {data.record_type === 'cng_storage' && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Facility Category</p>
                  <p className="font-medium">{data.facility_category ? data.facility_category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'N/A'}</p>
                </div>
              )}
              {data.record_type === 'trailer' && data.product_serial && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Product Serial</p>
                  <p className="font-mono text-sm">{data.product_serial}</p>
                </div>
              )}

              {/* Storage: Installation Position */}
              {data.record_type === 'cng_storage' && data.storage_installation_position && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Installation Position</p>
                  <p className="font-medium">{data.storage_installation_position.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                </div>
              )}

              {/* Storage: User Name; Trailer: Container Number */}
              {data.record_type === 'cng_storage' && data.user_name && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">User Name</p>
                  <p className="font-medium">{data.user_name}</p>
                </div>
              )}
              {data.record_type === 'trailer' && data.container_number && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Container Number</p>
                  <p className="font-mono text-sm">{data.container_number}</p>
                </div>
              )}

              {/* Capacity (Trailer/Storage) */}
              {(data.record_type === 'trailer' || data.record_type === 'cng_storage') && data.capacity && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Capacity</p>
                  <p className="font-medium">{data.capacity} m³</p>
                </div>
              )}

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Year</p>
                <p className="font-medium">{data.year || 'N/A'}</p>
              </div>

              {/* Vehicle-only: Chassis Number */}
              {data.record_type !== 'cng_storage' && data.chassis_number && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Chassis Number</p>
                  <p className="font-mono text-sm">{data.chassis_number}</p>
                </div>
              )}
            </div>

            {/* NGV System Origin - Only for Vehicles (not Trailer/Storage) */}
            {data.record_type !== 'trailer' && data.record_type !== 'cng_storage' && (
              <>
                <Separator />
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">NGV System Origin</p>
                    <div className="flex items-center gap-2">
                      <Wrench className="h-4 w-4 text-muted-foreground" />
                      <p className="font-medium">
                        {data.ngv_origin === 'oem' ? 'OEM (Factory Installed)' : 
                         data.ngv_origin === 'retrofitted' ? 'Retrofitted (After-market)' : 
                         data.ngv_origin || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {data.ngv_origin === 'retrofitted' && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Installer Name</p>
                      <p className="font-medium">{data.installer_name || 'Not Specified'}</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Inspection Details */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Inspection Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Inspection Date</p>
                <p className="font-medium flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  {formatDate(data.inspection_date)}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Valid Until</p>
                <p className={cn(
                  "font-bold text-lg",
                  isExpired ? "text-red-600" : "text-emerald-600"
                )}>{formatDate(data.valid_to)}</p>
              </div>
            </div>

            <Separator />

            {/* Inspector Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Inspector</p>
                <p className="font-medium flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  {data.inspector_name}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Inspector ID</p>
                <p className="font-mono font-medium">{data.inspector_license || 'N/A'}</p>
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Organization</p>
                <p className="font-medium flex items-center gap-2">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  {data.organization_name || 'Independent'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Test Results */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              Test Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {/* Visual Inspection */}
              {(() => {
                const result = getTestResultDisplay(data.visual_inspection_passed);
                const Icon = result.icon;
                return (
                  <div className={cn("p-3 rounded-lg", result.bg)}>
                    <p className="text-xs text-muted-foreground mb-1">Visual Inspection</p>
                    <div className="flex items-center gap-2">
                      <Icon className={cn("h-4 w-4", result.color)} />
                      <span className={cn("font-semibold", result.color)}>{result.text}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Gas Leak Test */}
              {(() => {
                const result = getTestResultDisplay(data.gas_leak_test_passed);
                const Icon = result.icon;
                return (
                  <div className={cn("p-3 rounded-lg", result.bg)}>
                    <p className="text-xs text-muted-foreground mb-1">Gas Leak Test</p>
                    <div className="flex items-center gap-2">
                      <Icon className={cn("h-4 w-4", result.color)} />
                      <span className={cn("font-semibold", result.color)}>{result.text}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Pressure Test */}
              {(() => {
                const result = getTestResultDisplay(data.pressure_test_passed);
                const Icon = result.icon;
                return (
                  <div className={cn("p-3 rounded-lg", result.bg)}>
                    <p className="text-xs text-muted-foreground mb-1">Pressure Test</p>
                    <div className="flex items-center gap-2">
                      <Icon className={cn("h-4 w-4", result.color)} />
                      <span className={cn("font-semibold", result.color)}>{result.text}</span>
                    </div>
                  </div>
                );
              })()}

              {/* NDT Test */}
              {(() => {
                const result = getTestResultDisplay(data.ndt_test_passed);
                const Icon = result.icon;
                return (
                  <div className={cn("p-3 rounded-lg", result.bg)}>
                    <p className="text-xs text-muted-foreground mb-1">NDT Test</p>
                    <div className="flex items-center gap-2">
                      <Icon className={cn("h-4 w-4", result.color)} />
                      <span className={cn("font-semibold", result.color)}>{result.text}</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Overall Result */}
            {data.overall_result && (
              <div className={cn(
                "mt-4 p-4 rounded-lg text-center",
                data.overall_result === 'approved' ? "bg-emerald-100" :
                data.overall_result === 'conditionally_approved' ? "bg-amber-100" :
                "bg-red-100"
              )}>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Overall Result</p>
                <p className={cn(
                  "font-bold text-lg uppercase",
                  data.overall_result === 'approved' ? "text-emerald-700" :
                  data.overall_result === 'conditionally_approved' ? "text-amber-700" :
                  "text-red-700"
                )}>
                  {data.overall_result.replace('_', ' ')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* CNG Cylinders */}
        {data.cylinders && data.cylinders.length > 0 && (
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Cylinder className="h-5 w-5 text-primary" />
                CNG Cylinders ({data.cylinders.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {data.cylinders.map((cyl, idx) => (
                  <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">#{idx + 1}</Badge>
                        <span className="font-mono font-semibold">{cyl.serial_number}</span>
                      </div>
                      {cyl.cylinder_type && (
                        <Badge variant="secondary">Type {cyl.cylinder_type}</Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {cyl.manufacturer && (
                        <div>
                          <span className="text-muted-foreground">Manufacturer: </span>
                          <span>{cyl.manufacturer}</span>
                        </div>
                      )}
                      {cyl.expiry_date && (
                        <div>
                          <span className="text-muted-foreground">Expires: </span>
                          <span>{cyl.expiry_date?.substring(0, 10)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer Note */}
        <div className="text-center text-xs text-muted-foreground py-6">
          <p>This is an official verification from the CNG Inspection Portal.</p>
          <p>For inquiries, contact Shields Engineering and Safety Solutions Company Limited</p>
          <a href="mailto:info@shieldsengineering.co.tz" className="text-primary hover:underline">
            info@shieldsengineering.co.tz
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-card/80 py-4">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© 2026 CNG Inspection Portal - Shields Engineering and Safety Solutions Company Limited</p>
        </div>
      </footer>
    </div>
  );
}
